/**
 * Tester feedback: a report with a snapshot of the current game attached, a screenshot of the
 * screen taken as the report opens (removable), and more images by paste or file pick.
 * Admins read reports at /admin/feedback.
 */
import { useEffect, useState, type ClipboardEvent } from 'react';
import { domToJpeg } from 'modern-screenshot';
import { FEEDBACK_CATEGORIES, FEEDBACK_MAX_IMAGES, FEEDBACK_MAX_LENGTH, t } from '@kadice/shared';
import { api } from '../api';
import type { Options } from '../options';
import type { GameClient } from '../useGameClient';
import { tuningSnapshot } from '../dice3d/tuning';

type Category = (typeof FEEDBACK_CATEGORIES)[number];

/** Longest edge of any image sent, in pixels. */
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;

/** Screenshot of the page as it is now, as a JPEG data URL (null if capture fails). */
export async function captureScreen(): Promise<string | null> {
  const root = document.querySelector<HTMLElement>('.shell') ?? document.body;
  const width = root.scrollWidth;
  try {
    return await domToJpeg(root, {
      quality: JPEG_QUALITY,
      scale: Math.min(1, MAX_EDGE / width),
      backgroundColor: getComputedStyle(document.body).backgroundColor,
    });
  } catch {
    return null;
  }
}

/** A pasted or picked image, downscaled and re-encoded as a JPEG data URL. */
async function shrinkImage(file: Blob): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

/** What the reporter's browser knows right now: the game as shown, recent notices and chat, options, screen. */
function snapshot(client: GameClient, options: Options): Record<string, unknown> {
  return {
    capturedAt: new Date().toISOString(),
    url: location.href,
    viewport: { width: innerWidth, height: innerHeight, dpr: devicePixelRatio },
    connection: client.connection,
    me: client.me,
    options,
    view: client.view,
    lobbyCount: client.lobbies.length,
    recentNotices: client.noticeLog.current,
    recentChat: client.chat.slice(-20),
    // Motion dice tuning saved on this device (Dice Lab), and any unsaved edits.
    diceTuning: tuningSnapshot(),
  };
}

export function FeedbackModal({
  client,
  options,
  screenshot,
  onClose,
}: {
  client: GameClient;
  options: Options;
  /** Taken just before the modal opened, so the modal itself isn't in it. */
  screenshot: string | null;
  onClose: () => void;
}) {
  const [category, setCategory] = useState<Category>('bug');
  const [body, setBody] = useState('');
  const [images, setImages] = useState<string[]>(screenshot ? [screenshot] : []);
  const [snap] = useState(() => snapshot(client, options));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const full = images.length >= FEEDBACK_MAX_IMAGES;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !sending && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, sending]);

  const addFiles = async (files: Iterable<Blob>) => {
    const picked = [...files].filter((f) => f.type.startsWith('image/'));
    if (picked.length === 0) return;
    try {
      const added = await Promise.all(picked.map(shrinkImage));
      setImages((cur) => [...cur, ...added].slice(0, FEEDBACK_MAX_IMAGES));
    } catch {
      setError(t('feedback.imageUnreadable'));
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    const files = [...e.clipboardData.items].map((i) => i.getAsFile()).filter((f): f is File => !!f);
    if (files.some((f) => f.type.startsWith('image/'))) {
      e.preventDefault();
      void addFiles(files);
    }
  };

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const { id } = await api.sendFeedback({ category, body, snapshot: snap, images });
      client.notify({ kind: 'success', text: t('feedback.sent', { id }) });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('feedback.failed'));
      setSending(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={() => !sending && onClose()}>
      <div
        className="modal feedback"
        role="dialog"
        aria-modal="true"
        aria-label={t('feedback.title')}
        onClick={(e) => e.stopPropagation()}
        onPaste={onPaste}
      >
        <div className="row">
          <h3>{t('feedback.title')}</h3>
          <span className="spacer" />
          <button onClick={onClose} disabled={sending}>
            {t('common.close')}
          </button>
        </div>

        <div className="row feedback-categories" role="radiogroup" aria-label={t('feedback.kind')}>
          {FEEDBACK_CATEGORIES.map((c) => (
            <label key={c}>
              <input type="radio" name="feedback-category" checked={category === c} onChange={() => setCategory(c)} />{' '}
              {t(`feedback.category.${c}`)}
            </label>
          ))}
        </div>

        <textarea
          value={body}
          maxLength={FEEDBACK_MAX_LENGTH}
          rows={6}
          autoFocus
          placeholder={t('feedback.placeholder')}
          onChange={(e) => setBody(e.target.value)}
        />

        <div className="feedback-images">
          {images.map((src, i) => (
            <div key={i} className="feedback-thumb">
              <img
                src={src}
                alt={i === 0 && screenshot === src ? t('feedback.screenshot') : t('feedback.image', { n: i + 1 })}
              />
              <button
                className="remove-bot"
                aria-label={t('feedback.removeImage')}
                title={t('feedback.removeImage')}
                onClick={() => setImages((cur) => cur.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ))}
          {!full && (
            <label className="feedback-add" title={t('feedback.addImageHint')}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                onChange={(e) => {
                  if (e.target.files) void addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              {t('feedback.addImage')}
            </label>
          )}
        </div>
        <p className="muted small">
          {screenshot && `${t('feedback.screenshotAttached')} `}
          {t('feedback.imagesHint', { max: FEEDBACK_MAX_IMAGES })}
        </p>

        <details className="feedback-snapshot">
          <summary className="muted small">
            {client.view
              ? t('feedback.gameInfoOf', { game: client.view.gameId, phase: client.view.phase })
              : t('feedback.gameInfo')}
          </summary>
          <pre>{JSON.stringify(snap, null, 2)}</pre>
        </details>

        {error && <p className="error">{error}</p>}
        <div className="row actions">
          <span className="spacer" />
          <button className="primary" disabled={sending || body.trim().length === 0} onClick={() => void send()}>
            {t(sending ? 'feedback.sending' : 'feedback.send')}
          </button>
        </div>
      </div>
    </div>
  );
}
